// FirstTripTutorial - Interactive onboarding for new users
// 3-4 steps, action-oriented, can be skipped or replayed
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  MapPin, ArrowRight, CheckCircle, Sparkles, 
  Car, Calculator, FileText, X, RotateCcw,
  Route, Fuel, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import confetti from 'canvas-confetti';

const FirstTripTutorial = ({ onComplete, forceShow = false }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Tutorial steps with translation keys
  const TUTORIAL_STEPS = [
    {
      id: 'welcome',
      icon: Sparkles,
      titleKey: 'tutorial.welcome.title',
      descriptionKey: 'tutorial.welcome.description',
      tips: [
        { icon: Route, textKey: 'tutorial.tips.preciseTracking' },
        { icon: Calculator, textKey: 'tutorial.tips.autoCalculations' },
        { icon: FileText, textKey: 'tutorial.tips.accountantExports' }
      ],
      action: null
    },
    {
      id: 'first-trip',
      icon: MapPin,
      titleKey: 'tutorial.firstTrip.title',
      descriptionKey: 'tutorial.firstTrip.description',
      tips: [
        { icon: MapPin, textKey: 'tutorial.tips.addressAutocomplete' },
        { icon: Fuel, textKey: 'tutorial.tips.fuelEstimate' },
        { icon: Clock, textKey: 'tutorial.tips.dateAmount' }
      ],
      action: 'navigate-trips',
      buttonTextKey: 'tutorial.createTrip'
    },
    {
      id: 'track-expenses',
      icon: Calculator,
      titleKey: 'tutorial.expenses.title',
      descriptionKey: 'tutorial.expenses.description',
      tips: [
        { icon: Fuel, textKey: 'tutorial.tips.fuelTollParking' },
        { icon: Sparkles, textKey: 'tutorial.tips.aiScan' },
        { icon: FileText, textKey: 'tutorial.tips.autoCategories' }
      ],
      action: 'navigate-expenses',
      buttonTextKey: 'tutorial.addExpense'
    },
    {
      id: 'success',
      icon: CheckCircle,
      titleKey: 'tutorial.success.title',
      descriptionKey: 'tutorial.success.description',
      tips: [
        { icon: FileText, textKey: 'tutorial.tips.pdfExcelExports' },
        { icon: Calculator, textKey: 'tutorial.tips.mileageAllowance' },
        { icon: CheckCircle, textKey: 'tutorial.tips.compliance2026' }
      ],
      action: 'finish',
      buttonTextKey: 'tutorial.start'
    }
  ];

  // Check if tutorial should be shown - with robust localStorage check
  useEffect(() => {
    // Always check localStorage on mount
    const checkTutorial = () => {
      const tutorialCompleted = localStorage.getItem('optitrajet_tutorial_completed');
      const tutorialSkipped = localStorage.getItem('optitrajet_tutorial_skipped');
      
      if (forceShow) {
        setIsVisible(true);
        setCurrentStep(0);
        return;
      }
      
      // Don't show if already completed or skipped
      if (tutorialCompleted === 'true' || tutorialSkipped === 'true') {
        setIsVisible(false);
        return;
      }
      
      // Show tutorial if never completed and user just logged in
      // Small delay for smoother UX after login
      const timer = setTimeout(() => {
        // Double check localStorage before showing
        const completed = localStorage.getItem('optitrajet_tutorial_completed');
        const skipped = localStorage.getItem('optitrajet_tutorial_skipped');
        if (completed !== 'true' && skipped !== 'true') {
          setIsVisible(true);
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    };
    
    checkTutorial();
  }, [forceShow]);

  const handleNext = () => {
    setHasInteracted(true);
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAction = () => {
    const step = TUTORIAL_STEPS[currentStep];
    
    if (step.action === 'navigate-trips') {
      localStorage.setItem('optitrajet_tutorial_step', 'trips');
      navigate('/trips');
      setIsVisible(false);
    } else if (step.action === 'navigate-expenses') {
      localStorage.setItem('optitrajet_tutorial_step', 'expenses');
      navigate('/expenses');
      setIsVisible(false);
    } else if (step.action === 'finish') {
      // Confetti celebration!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#6366f1', '#8b5cf6', '#a855f7']
      });
      
      localStorage.setItem('optitrajet_tutorial_completed', 'true');
      localStorage.removeItem('optitrajet_tutorial_skipped');
      setIsVisible(false);
      if (onComplete) onComplete();
    } else {
      handleNext();
    }
  };

  const handleSkip = () => {
    // Persist skip immediately
    try {
      localStorage.setItem('optitrajet_tutorial_skipped', 'true');
    } catch (e) {
      console.error('Failed to save tutorial skip:', e);
    }
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleClose = () => {
    // Always skip on close (X button)
    try {
      localStorage.setItem('optitrajet_tutorial_skipped', 'true');
    } catch (e) {
      console.error('Failed to save tutorial skip:', e);
    }
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  // Get profession-specific welcome message
  const getProfessionWelcome = () => {
    const profession = user?.settings?.profession;
    if (profession) {
      return t(`tutorial.professions.${profession}`, t('tutorial.professions.default'));
    }
    return t('tutorial.professions.default');
  };

  if (!isVisible) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        data-testid="first-trip-tutorial"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Progress bar */}
          <div className="h-1 bg-gray-100 dark:bg-gray-800">
            <motion.div 
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>

          {/* Content */}
          <div className="p-8">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {TUTORIAL_STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'w-8 bg-gradient-to-r from-indigo-500 to-purple-500' 
                      : idx < currentStep
                        ? 'bg-indigo-400'
                        : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>

            {/* Icon */}
            <motion.div
              key={`icon-${currentStep}`}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', damping: 15 }}
              className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30"
            >
              <StepIcon className="w-10 h-10 text-white" />
            </motion.div>

            {/* Title */}
            <motion.h2
              key={`title-${currentStep}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2"
            >
              {t(step.titleKey)}
            </motion.h2>

            {/* Profession badge on welcome */}
            {currentStep === 0 && user?.settings?.profession && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex justify-center mb-4"
              >
                <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium">
                  {getProfessionWelcome()}
                </span>
              </motion.div>
            )}

            {/* Description */}
            <motion.p
              key={`desc-${currentStep}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center text-gray-600 dark:text-gray-400 mb-6 leading-relaxed"
            >
              {t(step.descriptionKey)}
            </motion.p>

            {/* Tips */}
            <motion.div
              key={`tips-${currentStep}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-3 gap-3 mb-8"
            >
              {step.tips.map((tip, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + idx * 0.1 }}
                  className="flex flex-col items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-xl"
                >
                  <tip.icon className="w-5 h-5 text-indigo-500 mb-2" />
                  <span className="text-xs text-gray-600 dark:text-gray-400 text-center">{t(tip.textKey)}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Actions */}
            <div className="flex gap-3">
              {currentStep > 0 && currentStep < TUTORIAL_STEPS.length - 1 && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  className="flex-1"
                >
                  {t('tutorial.previous')}
                </Button>
              )}
              
              {currentStep === 0 && (
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="flex-1 text-gray-500"
                >
                  {t('tutorial.skip')}
                </Button>
              )}
              
              <Button
                onClick={step.action ? handleAction : handleNext}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                data-testid="tutorial-next-btn"
              >
                {step.buttonTextKey ? t(step.buttonTextKey) : t('common.next')}
                {!step.action && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>

            {/* Trial reminder on last step */}
            {currentStep === TUTORIAL_STEPS.length - 1 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4"
              >
                {t('tutorial.trialReminder')}
              </motion.p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to replay tutorial from settings
export const useTutorial = () => {
  const [showTutorial, setShowTutorial] = useState(false);
  
  const replayTutorial = () => {
    localStorage.removeItem('optitrajet_tutorial_completed');
    localStorage.removeItem('optitrajet_tutorial_skipped');
    setShowTutorial(true);
  };
  
  const closeTutorial = () => {
    setShowTutorial(false);
  };
  
  return { showTutorial, replayTutorial, closeTutorial };
};

export default FirstTripTutorial;
